import { Alert, Box, Grid, TextField, Typography } from '@mui/material';
import get from 'lodash/get';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMixpanel } from 'react-mixpanel-browser';

import { usePostPassword, useUserAccount } from '@/api/userAccount';
import { FormInputText } from '@/components/form/FormInputText';
import { ResetUpdateButtonGroup } from '@/components/userAccount/ResetUpdateButtonGroup';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { PASSWORD_MIN_LENGTH, PASSWORD_POLICY_HINT, getPasswordPolicyMessages } from '@/utils/passwordPolicyHelpers';

const defaultPasswordFieldsValues = {
    newPassword: '',
    reEnterNewPassword: '',
};

export const PasswordSection = () => {
    const { data: accountData } = useUserAccount();
    const { mutateAsync: postPassword } = usePostPassword();
    const mixpanel = useMixpanel();
    const [passwordPolicyMessages, setPasswordPolicyMessages] = useState<string[]>([]);

    const {
        control: passwordControl,
        reset: passwordReset,
        handleSubmit: passwordHandleSubmit,
        formState: passwordFormState,
        getValues: getPasswordFormValues,
    } = useForm<typeof defaultPasswordFieldsValues>({
        defaultValues: defaultPasswordFieldsValues,
        mode: 'onChange',
    });
    useUnsavedChangesGuard(passwordFormState.isDirty, 'password-section');

    const onPasswordReset = () => {
        passwordReset(defaultPasswordFieldsValues);
        setPasswordPolicyMessages([]);
    };

    const onPasswordSubmit = async (formData: typeof defaultPasswordFieldsValues) => {
        setPasswordPolicyMessages([]);
        try {
            await postPassword(formData);
            mixpanel?.track('User Account - Password Updated');
            onPasswordReset();
        } catch (err) {
            mixpanel?.track('User Account - Password Update Failed');
            setPasswordPolicyMessages(getPasswordPolicyMessages(err));
            console.error(err);
        }
    };

    return (
        <Box data-testid="user-account-password-section">
            <Typography color="text.secondary" variant="h5" sx={{ mb: 2 }}>
                Password change
            </Typography>
            <Grid container spacing={3} component="form">
                <TextField
                    sx={{ display: 'none' }}
                    name="username"
                    value={get(accountData, 'data.user.username', '')}
                    autoComplete="username"
                    disabled
                />
                <Grid size={{ xs: 12, md: 6 }}>
                    <FormInputText
                        name="newPassword"
                        control={passwordControl}
                        label="New Password"
                        defaultHelperText={PASSWORD_POLICY_HINT}
                        inputFieldProps={{
                            id: 'newPassword',
                            type: 'password',
                            size: 'medium',
                            autoComplete: 'new-password',
                        }}
                        controllerProps={{
                            rules: {
                                required: 'Field is required',
                                minLength: {
                                    value: PASSWORD_MIN_LENGTH,
                                    message: `Minimum ${PASSWORD_MIN_LENGTH} characters`,
                                },
                            },
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <FormInputText
                        name="reEnterNewPassword"
                        control={passwordControl}
                        label="Reenter New Password"
                        inputFieldProps={{
                            id: 'reEnterNewPassword',
                            type: 'password',
                            size: 'medium',
                            autoComplete: 'new-password',
                        }}
                        controllerProps={{
                            rules: {
                                required: 'Field is required',
                                validate: (value: string) =>
                                    value === getPasswordFormValues('newPassword') || 'Passwords do not match',
                            },
                        }}
                    />
                </Grid>
            </Grid>
            {passwordPolicyMessages.map((message, index) => (
                <Alert key={`${index}-${message}`} severity="error" sx={{ mt: 2 }}>
                    {message}
                </Alert>
            ))}
            <ResetUpdateButtonGroup
                onReset={onPasswordReset}
                onSubmit={passwordHandleSubmit(onPasswordSubmit)}
                isResetDisabled={!passwordFormState.isDirty || passwordFormState.isSubmitting}
                isUpdateDisabled={!passwordFormState.isDirty || passwordFormState.isSubmitting}
                isSubmitting={passwordFormState.isSubmitting}
            />
        </Box>
    );
};
